import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Search, Download } from "lucide-react";
import type { BacktestInvoiceRow } from "@/types/backtest";
import { formatCurrency } from "@/lib/currency";
import { exportBacktestRowsCsv } from "@/lib/csvExport";

type StatusFilter = "all" | "missed" | "captured" | "no_terms" | "error";

const PAGE_SIZE = 50;

function rowStatus(row: BacktestInvoiceRow): StatusFilter {
  if (row.parse_error) return "error";
  if (!row.was_eligible) return "no_terms";
  if (row.discount_captured) return "captured";
  return "missed";
}

function StatusBadge({ status }: { status: StatusFilter }) {
  const config = {
    missed: {
      label: "Missed",
      className: "bg-destructive/10 text-destructive border-destructive/20",
    },
    captured: { label: "Captured", className: "bg-green-50 text-green-700 border-green-200" },
    no_terms: { label: "No Terms", className: "bg-muted text-muted-foreground" },
    error: { label: "Error", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    all: { label: "", className: "" },
  };
  const c = config[status];
  return (
    <Badge variant="outline" className={`text-[11px] ${c.className}`}>
      {c.label}
    </Badge>
  );
}

interface Props {
  rows: BacktestInvoiceRow[];
}

export function InvoiceResultsTable({ rows }: Props) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);

  const filtered = rows.filter((row) => {
    const matchesStatus = statusFilter === "all" || rowStatus(row) === statusFilter;
    const matchesSearch =
      !search ||
      (row.supplier_name ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (row.invoice_number ?? "").toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleFilterChange = (v: string) => {
    setStatusFilter(v as StatusFilter);
    setPage(1);
  };

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search supplier or invoice #…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="h-8 text-sm w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All rows ({rows.length})</SelectItem>
            <SelectItem value="missed">
              Missed ({rows.filter((r) => rowStatus(r) === "missed").length})
            </SelectItem>
            <SelectItem value="captured">
              Captured ({rows.filter((r) => rowStatus(r) === "captured").length})
            </SelectItem>
            <SelectItem value="no_terms">
              No Terms ({rows.filter((r) => rowStatus(r) === "no_terms").length})
            </SelectItem>
            <SelectItem value="error">
              Errors ({rows.filter((r) => rowStatus(r) === "error").length})
            </SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          {filtered.length} row{filtered.length !== 1 ? "s" : ""}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5 ml-auto"
          onClick={() => exportBacktestRowsCsv(filtered)}
          disabled={filtered.length === 0}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Invoice #</TableHead>
              <TableHead>Invoice Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Terms</TableHead>
              <TableHead>Payment Date</TableHead>
              <TableHead>Deadline</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Savings</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-8">
                  No rows match the current filter.
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => {
                const status = rowStatus(row);
                return (
                  <TableRow key={row.id} className="text-xs">
                    <TableCell className="text-muted-foreground">{row.row_number}</TableCell>
                    <TableCell className="font-medium">{row.supplier_name ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.invoice_number ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.invoice_date ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.invoice_amount != null ? formatCurrency(row.invoice_amount) : "—"}
                    </TableCell>
                    <TableCell>
                      {row.payment_terms ? (
                        <span className="font-mono">{row.payment_terms}</span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.actual_payment_date ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.discount_deadline ?? "—"}
                    </TableCell>
                    <TableCell>
                      {status !== "all" && (
                        <div className="space-y-0.5">
                          <StatusBadge status={status} />
                          {row.parse_error && (
                            <p className="text-[10px] text-muted-foreground">{row.parse_error}</p>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {status === "missed" && row.missed_savings > 0 ? (
                        <span className="text-destructive">
                          {formatCurrency(row.missed_savings)}
                        </span>
                      ) : status === "captured" && row.captured_savings > 0 ? (
                        <span className="text-green-700">
                          {formatCurrency(row.captured_savings)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Page {safePage} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
